import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card/Card';
import { SectionLabel } from '@/components/ui/section-label';
import { useTranslation } from '@/hooks/useTranslation';
import type { SignalsSeriesPayload } from '@/features/signals/type/SignalsTypes';
import { SignalChartStack } from '@/features/signals/components/SignalChartStack';
import { SignalSkeleton } from '@/features/signals/components/SignalSkeleton';

const CHART_ROW_HEIGHT = 100;

interface MLSignalCardProps {
  signal: SignalsSeriesPayload | null;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}

/**
 * "Signals used for inference" card — all 4 model-input channels (power +
 * X/Y/Z follow error) over the passes the model read, no interactive controls. The full interactive chart
 * (chips, stack/overlay, samples) lives on the dedicated /signals page,
 * linked from the header.
 */
export const MLSignalCard: FC<MLSignalCardProps> = ({ signal, isLoading, error, onRetry }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card>
        <SignalSkeleton />
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <EmptyState
          tone="error"
          size="compact"
          title={t('mlRul.signal.errorTitle')}
          description={error}
          action={
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t('mlRul.signal.errorRetry')}
            </Button>
          }
        />
      </Card>
    );
  }

  if (!signal) {
    return null;
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <SectionLabel>{t('mlRul.signal.badge')}</SectionLabel>
          <span className="text-xs text-muted-foreground">
            {signal.partId
              ? t('mlRul.signal.helperPart', { passes: signal.passes.length, part: signal.partId })
              : t('mlRul.signal.helper', { passes: signal.passes.length })}
          </span>
        </div>
        <Button
          variant="link"
          size="inline"
          className="text-xs"
          onClick={() => navigate('/signals')}
        >
          {t('mlRul.signal.openLink')}
        </Button>
      </div>

      <SignalChartStack channels={signal.channels} startTime={signal.startTime} rowHeight={CHART_ROW_HEIGHT} />
    </Card>
  );
};
